import React, {useEffect, useState} from 'react';
import debug from 'debug';
import {connect} from 'react-redux';
import {
  Button,
  Checkbox,
  DatePicker,
  DescriptiveText,
  Dropdown,
  LoadingSpinner,
  NumberInput,
  RadioSet,
  Row,
  SectionTitle,
} from '../../../shared';
import {BenchmarkSelector} from '../../../advisor';
import axiosInstance from '../../../shared/utils/axios';
import {_openModal} from '../../../shared/reducers/modals';
import moment from 'moment';
import lazy from 'lazy.js';
import {_getPeriods, _getUnreadReports, isEntitled} from '../../../shared/store';
import PropTypes from 'prop-types';
import Promise from 'bluebird';
import {calculateFixedStartDate, parseErrorMessage} from '../../../shared/utils/utils';

const log = debug('circleblack:report:modular');
// ETODO
// Can't perform a React state update on an unmounted component.
// Invalid prop `detailed` supplied to `ModularReportParameters`.

const ModularReportParameters = props => {
  const {
    report,
    detailedContext,
    detailed,
    initialContainerID,
    setCanGenerate,
    setErrorMessage,
    setSuccessMessage,
    setStep,
    handleError,
    openModal,
    session,
    unreadReportsTimeoutID,
    unreadReports,
    reportPageVisitTimestamp,
    getUnreadReports,
    setProcessing,
    entity,
  } = props;

  const [loading, setLoading] = useState(true);
  const [isSavingDefaultSettings, setIsSavingDefaultSettings] = useState(false);

  const [params, setParams] = useState(null);
  const [canShare, setCanShare] = useState(null);
  const [performanceMethodology, setPerformanceMethodology] = useState('dwr');
  const [performanceFeeType, setPerformanceFeeType] = useState('gross');
  const [benchmark, setBenchmark] = useState(null);
  const [timeType, setTimeType] = useState('FIXED');
  const [timePeriod, setTimePeriod] = useState(null);
  const [minDate, setMinDate] = useState(null);
  // const [maxDate, setMaxDate] = useState(null);
  const [lastBusinessDay, setLastBusinessDay] = useState(null);
  const [minTaxLotDate, setMinTaxLotDate] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [isBillingInvoice, setIsBillingInvoice] = useState(false);

  const oldPerf = isEntitled([
    {
      group: 'performance',
      feature: 'core',
      access: 'r',
    },
  ]) && !isEntitled([
    {
      group: 'reports',
      feature: 'performance_report',
      access: 'r',
    },
  ]);
  const entitledBenchmarks = isEntitled([{group: 'benchmarks', feature: 'core', access: 'r'}]);
  const perfReport = report.name === 'Performance Report';
  const yesterday = moment().subtract(1, 'd').format('YYYY-MM-DD');

  const perfReportEntitlement = isEntitled([{group: 'reports', feature: 'performance_report', access: 'r'}]);

  const updateParameterValue = (name, value) => {
    if (perfReport && (name === 'multiPeriodPerformanceTablePage' || name === 'activitySummaryPage')) {
      const otherParameterName = name === 'multiPeriodPerformanceTablePage' ? 'activitySummaryPage' : 'multiPeriodPerformanceTablePage';
      if (value) {
        if (!perfReportEntitlement) {
          setParams(current => ({
            ...current,
            [otherParameterName]: {...current[otherParameterName], value: false, disabled: true},
            [name]: {...current[name], value: value},
          }));
        } else {
          setParams(current => ({...current, [name]: {...current[name], value: value}}));
        }
      } else {
        setParams(current => ({
          ...current,
          [otherParameterName]: {...current[otherParameterName], disabled: false},
          [name]: {...current[name], value: value},
        }));
      }
    } else if (perfReport
      && !value
      && (name === 'holdingsByAccountPage' || name === 'holdingsByAssetClassPage')
      && params.positionPerformance?.value) {
      const otherParameterName = name === 'holdingsByAccountPage' ? 'holdingsByAssetClassPage' : 'holdingsByAccountPage';
      if (params[otherParameterName].value) {
        setParams(current => ({...current, [name]: {...current[name], value: value}}));
      } else {
        setParams(current => ({
          ...current,
          positionPerformance: {...current.positionPerformance, value: false},
          [name]: {...current[name], value: value},
        }));
      }
    } else {
      setParams(current => ({...current, [name]: {...current[name], value: value}}));
    }
  };

  const initParams = (prefs) => {
    log('initParams');
    const parameters = lazy(report.parameters).indexBy('name').toObject();
    if (perfReport) {
      parameters.activitySummaryPage.disabled = !oldPerf;
      parameters.activityGraphPage.default = !oldPerf;
      parameters.netInflows.default = !oldPerf;
      parameters.netOutflows.default = !oldPerf;
      parameters.netReturn.default = !oldPerf;
      parameters.interestPaid.default = !oldPerf;
      parameters.dividendsPaid.default = !oldPerf;
      parameters.advisoryFees.default = !oldPerf;
    }
    for (let s = 0; s < report.settings.length; s++) {
      const parameter = lazy(parameters).findWhere({id: report.settings[s].parameterID});
      if (parameter && ((report.settings[s].for.level > parameter.level) || (!parameter.level && !parameter.enforce))) {
        if (parameter.name === 'share' && !isEntitled([
          {
            group: 'reports',
            feature: 'email',
            access: 'r',
          },
        ])) {
          parameter.setting = parameter.default;
          continue;
        }
        parameter.setting = report.settings[s].value;
        parameter.level = report.settings[s].for.level;
        parameter.enforce = report.settings[s].enforce;
      }
    }
    if (perfReport) {
      parameters.dollarWeighted.setting = prefs.performanceMethodology !== 'twr';
      parameters.timeWeighted.setting = prefs.performanceMethodology === 'twr';
      parameters.grossFees.setting = prefs.performanceFeeType !== 'net';
      parameters.netFees.setting = prefs.performanceFeeType === 'net';

      if (!perfReportEntitlement) {
        parameters.activitySummaryPage.default = false;
        parameters.activitySummaryPage.setting = false;
        parameters.activitySummaryPage.disabled = true;

        parameters.multiPeriodPerformanceTablePage.default = true;
      } else {
        parameters.activitySummaryPage.disabled = false;
      }
    }
    if (entity?.entity && entity?.entityID && parameters.entity && parameters.entityID && parameters.netWorthPage) {
      parameters.entity.default = entity.entity;
      parameters.entityID.default = entity.entityID;
    }
    for (let p in parameters) {
      if (parameters.hasOwnProperty(p)) {
        const parameter = parameters[p];
        if (parameter.type === 'number') {
          parameter.value = (parameter.setting !== undefined ? parseFloat(parameter.setting) : parameter.default);
        } else {
          parameter.value = (parameter.setting !== undefined ? parameter.setting : parameter.default);
        }
      }
    }
    setParams(parameters);
  };

  const checkCanShare = () => {
    let shareable;
    let op;

    if (!isEntitled([
      {
        group: 'reports',
        feature: 'share',
        access: 'r',
      },
      {
        group: 'advisor',
        feature: 'client_engagement',
        access: 'r',
      },
    ], true)) {
      shareable = false;
    } else if (detailedContext === 'ADVISOR_GROUP') {
      if (detailed === 'ALL') {
        op = axiosInstance.get(`/containers/${initialContainerID}/groups/households/count`)
          .then(res => {shareable = !res.data || res.data !== 1;});
      } else {
        shareable = !detailed.some(d => d.shareDisabled);
      }
    } else {
      shareable = true;
    }

    return Promise.resolve(op || null)
      .then(() => {
        setCanShare(shareable);
        if (!shareable) {
          updateParameterValue('share', 'NONE');
        }
      });
  };

  // ETODO these parameter and variable names are bad
  const setUnrealizedGainsDisabled = (asOfDate, businessDay = lastBusinessDay, taxLotDate = minTaxLotDate) => {
    if (!isEntitled([{group: 'reports', feature: 'holdingsAnalysis', access: 'r'}])) {
      return null;
    }
    setParams(current => {
      const asOfDateMoment = moment(asOfDate, 'YYYY-MM-DD');
      const formattedMinDate = moment().startOf('year').subtract(1, 'd').format('YYYY-MM-DD');
      const formattedLastBusinessDay = moment(businessDay, 'YYYY-MM-DD');
      const formattedMinTaxLotDate = taxLotDate && moment(taxLotDate, 'YYYY-MM-DD');
      const disabled = !formattedMinTaxLotDate
        || asOfDate < formattedMinDate
        || (asOfDateMoment.isAfter(formattedMinTaxLotDate) && formattedMinTaxLotDate !== formattedLastBusinessDay);
      const wasDisabled = current.unrealizedGainLossValue.disabled;

      current.unrealizedGainLossValue.disabled = disabled;
      current.unrealizedGainLossPercentage.disabled = disabled;
      if (disabled) {
        current.unrealizedGainLossValue.value = false;
        current.unrealizedGainLossPercentage.value = false;
      } else if (wasDisabled) {
        current.unrealizedGainLossValue.value = true;
        current.unrealizedGainLossPercentage.value = true;
      }
      return current;
    });
  };

  const getBillingDocuments = (asOfDate) => Promise.map(
    detailed,
    row => axiosInstance.get('/documents/library', {
      params: {
        sort: '-createdAt',
        cat: 'Billing Invoice',
        limit: 1,
        shareApproval: '!DENIED',
        createdAt: {lt: asOfDate},
        assocHouseholdID: row.id,
      },
    })
      .then(res => res.data)
  )
    .then((results) => {
      const index = results.findIndex(item => item.length);
      if (index !== -1) {
        setIsBillingInvoice(true);
      } else {
        updateParameterValue('billingInvoice', false);
        setIsBillingInvoice(false);
      }
    });

  const setBillingInvoice = (asOfDate) => {
    if (detailedContext === 'HOUSEHOLD' && detailed && report.parameters.find(item => item.name === 'billingInvoice') && isEntitled([{group: 'partners', feature: 'billFin', access: 'r'}])) {
      getBillingDocuments(asOfDate ? moment(asOfDate, 'YYYY-MM-DD').add(2, 'd').format('YYYY-MM-DD') : undefined);
    }
  };

  const initTimePeriods = () => Promise.map(
    detailed === 'ALL' ? [{containerID: initialContainerID}] : detailed,
    row => axiosInstance.get(`/containers/${row.containerID}/dates`).then(res => res.data),
    {concurrency: 3}
  )
    .then(results => {
      const dates = lazy(results).flatten().sort().uniq();

      if (!dates.size()) {
        return handleError('The selected context has no valid data and a report can not be generated.');
      }

      const _minDate = dates.min();
      const _maxDate = isEntitled([{group: 'reports', feature: 'intraday', access: 'r'}]) ? dates.max() : yesterday;
      const _periods = lazy(props.periods)
        .filter(period => period.id === 'MAX' || (period.start >= _minDate && period.days < dates.size()))
        .map(period => period.id === 'MAX' ? {
          ...period,
          start: _minDate,
          days: moment(_maxDate, 'YYYY-MM-DD').diff(moment(_minDate, 'YYYY-MM-DD'), 'd'),
        } : period)
        .sortBy('days');

      const _timePeriod = _periods.filter(p => ['MONTHS_3', 'MONTHS_1'].includes(p.id)).last() || _periods.findWhere({id: 'MAX'});

      setParams(current => {
        current.startDate.value = moment().subtract(_timePeriod.days, 'day').format('YYYY-MM-DD');
        current.asOfDate.value = _maxDate;
        return current;
      });
      setTimePeriod(_timePeriod);
      setMinDate(_minDate);
      // setMaxDate(_maxDate);
      setPeriods(_periods.toArray());

      if (isEntitled([{group: 'reports', feature: 'holdingsAnalysis', access: 'r'}]) && !lastBusinessDay) {
        log('session', session);
        return Promise.join(
          axiosInstance.get('/periods/lastBusinessDay')
            .then(res => res.data),
          axiosInstance.get(`/firms/${session.profile.firm.id}/minTaxLotDate`)
            .then(res => res.data),
          (_lastBusinessDay, _minTaxLotDate) => {
            setLastBusinessDay(_lastBusinessDay);
            setMinTaxLotDate(_minTaxLotDate);
            setUnrealizedGainsDisabled(_maxDate, _lastBusinessDay, _minTaxLotDate);
            return _maxDate;
          }
        );
      }
      return _maxDate;
    })
    .catch(err => {
      log('error', err);
      throw err;
    });

  const canGenerate = () => {
    if (!params || isSavingDefaultSettings) {
      return false;
    }
    if (!Object.keys(params).length) {
      return true;
    }
    const holdingsAnalysisProps = [
      'quantity',
      'price',
      'marketValue',
      'unrealizedGainLossValue',
      'unrealizedGainLossPercentage',
      'estimatedAnnualIncome',
      'yield',
    ];

    log('canGenerate', 'called');
    if (report.name === 'Performance Report' && ((!params.dollarWeighted.value && !params.timeWeighted.value)
      || (!params.grossFees.value && !params.netFees.value))) {
      return false;
    }
    if (params.targetExposurePage && params.targetExposurePage.value) {
      const currentTargetPercent = Object.keys(params)
        .filter(paramKey => paramKey.slice(-6) === 'Target')
        .reduce((total, paramKey) => total + ((params[paramKey] && params[paramKey].value) || 0), 0);
      if (currentTargetPercent !== 100) {
        return false;
      }
    }

    if (params.holdingsAnalysisPage && params.holdingsAnalysisPage.value) {
      if (!lazy(params).pick(holdingsAnalysisProps).pluck('value').reduce((x, y) => x || y)) {
        return false;
      }
    }

    if (perfReport && entitledBenchmarks && !benchmark) {
      return false;
    }

    log(
      'canGenerate',
      params,
      !!lazy(Object.keys(params))
        .filter(paramKey => paramKey.slice(-4) === 'Page')
        .map(k => params[k])
        .pluck('value')
        .reduce((x, y) => x || y)
    );

    return lazy(Object.keys(params))
      .filter(paramKey => paramKey.slice(-4) === 'Page')
      .map(k => params[k])
      .pluck('value')
      .reduce((x, y) => x || y);
  };

  const generateReport = event => {
    event.preventDefault();
    // ETODO what is this?
    // var validStartDate = true;
    // $scope.params.period.value = $scope.selected.period.id;

    if (params.startDate.value < minDate) {
      return setErrorMessage('Reports can not be generated for the time period you have selected. Please try another period.');
    }

    if (moment(params.asOfDate.value, 'YYYY-MM-DD').isBefore(params.startDate.value, 'YYYY-MM-DD')) {
      return setErrorMessage('Cannot generate report for an end date before the start date.');
    }
    setProcessing(true);

    // if (validStartDate) { // ETODO what is this?
    log('Containers', detailed);
    return axiosInstance.post('/reports/' + report.name, {
      containerIDs: detailed === 'ALL' ? [initialContainerID] : detailed.map(d => d.containerID),
      selectedAll: detailed === 'ALL' && detailedContext,
      parameters: Object.values(params),
    })
      .then(() => {
        let message = 'Your reports have been submitted for generation. Once everything is completed you will be notified'
          + ' of the status and can view the generated reports in the Reports list or in your Document Vault.';

        if (params.share && params.share.value !== 'NONE') {
          message += ' The reports will also be shared with the respective households.';
        }
        setProcessing(false);
        setSuccessMessage(message);
        setStep('END');

        getUnreadReports(unreadReportsTimeoutID, unreadReports, reportPageVisitTimestamp, 30000);
      })
      .catch(err => {
        setErrorMessage(parseErrorMessage(err));
        setProcessing(false);
      });
  };

  const onlyAsOfDate = () => {
    let sum = 0;
    const paramKeys = Object.keys(params);
    for (let i = 0; i < paramKeys.length; i++) {
      if ((paramKeys[i].slice(-4) === 'Page') || (paramKeys[i].slice(-6) === 'Target')) {
        switch (paramKeys[i]) {
          case 'holdingsByAccountPage':
          case 'holdingsByAssetClassPage':
          case 'assetClassesPage':
          case 'assetClassDetailedPage':
          case 'targetExposurePage':
          case 'stockSectorPage':
          case 'bondSectorPage':
          case 'geographicPage':
          case 'stocksTarget':
          case 'bondsTarget':
          case 'cashTarget':
          case 'alternativesTarget':
            if (params[paramKeys[i]].value !== undefined) {
              sum += !!params[paramKeys[i]].value;
            }
            break;
          default:
            if (params[paramKeys[i]].value !== undefined) {
              if (!!params[paramKeys[i]].value) {
                return true;
              }
            }
            break;
        }
      }
    }
    return !sum;
  };

  const showStartAndPeriod = () => {
    if (onlyAsOfDate()) {
      return true;
    }
    const max = props.periods[0];
    if (timePeriod.id !== max.id || timeType !== 'FIXED') {
      setTimeType('FIXED');
      setTimePeriod(max);
      updateParameterValue('startDate', minDate);
    }
    return false;
  };

  const renderCheckbox = (paramName, help) => {
    log('renderCheckbox', 'rendering', paramName);
    if (!params[paramName]) {
      return null;
    }
    const param = {...params[paramName]};
    return <Checkbox id={param.id}
                     dataPoint={{name: param.name, value: !!param.value}}
                     onChange={(name, value) => {
                       updateParameterValue(name, !!value);
                     }}
                     title={param.displayName}
                     help={help}
                     disabled={param.disabled}/>;
  };

  const renderTargetInput = targetName => params[targetName]
    && <NumberInput title={params[targetName].displayName}
                    type={'percent'}
                    decimalPlaces={2}
                    dataPoint={{
                      name: targetName,
                      value: params[targetName].value / 100,
                    }}
                    placeholder={'Enter Target %...'}
                    onBlur={(name, value) => {
                      updateParameterValue(name, value * 100);
                    }}/>;

  const changeTimePeriod = (name, p) => {
    updateParameterValue('startDate', calculateFixedStartDate(minDate, params.asOfDate.value, p.value));
    setTimePeriod(p.value);
  };

  const updatePerfMethodology = (name, value) => {
    updateParameterValue('dollarWeighted', value !== 'twr');
    updateParameterValue('timeWeighted', value === 'twr');
    setPerformanceMethodology(value);
  };

  const updatePerfFeeType = (name, value) => {
    updateParameterValue('grossFees', value !== 'net');
    updateParameterValue('netFees', value === 'net');
    setPerformanceFeeType(value);
  };

  const updateBenchmark = b => {
    setBenchmark(b);
    updateParameterValue('benchmark', b.id);
  };

  const saveSettings = () => {
    setIsSavingDefaultSettings(true);
    return Promise.each(
      Object.values(params)
        .filter(param => param.canSave)
        .filter(param => ![
          'startDate',
          'asOfDate',
        ].includes(param.name)) // ETODO Replace by updating reports.parameters setting canSave to 0 for these
        .map(param => ({id: param.id, value: param.value}))
        .filter(param => param.value != null),
      param => axiosInstance.put(`/reports/${report.name}`, {
        parameter: param.id,
        value: param.value,
        for: {level: 5, id: session.profile.id},
        report: report.id,
      })
    )
      .then(() => {
        setIsSavingDefaultSettings(false);
        return openModal({
          type: 'MessageModal',
          title: 'Saved Settings',
          body: 'Settings saved successfully.',
        });
      })
      .catch(() => {
        // ETODO issue with saved confirmation pops up on complete no matter what page you are on in the UI
        setIsSavingDefaultSettings(false);
        setErrorMessage('There was a problem saving settings. Please try again.'
          + 'If the issue persists please contact Client Services to resolve the issue.');
      });
  };

  useEffect(() => props.getPeriods(), []);

  useEffect(() => {
    if (!(props.periods && props.periods.length)) {
      return;
    }
    return axiosInstance.get(`/preferences/advisors/${session.profile.id}/panels/advisorPreferences`)
      .then(res => {
        const collection = res.data.collections.find(c => c.title === 'performanceType');
        const performanceMethodologyGroup = collection && collection.groups.find(g => g.title === 'performanceMethodology');
        const performanceFeeTypeGroup = collection && collection.groups.find(g => g.title === 'performanceFeeType');
        const performanceMethodologyCore = performanceMethodologyGroup && performanceMethodologyGroup.cores[0];
        const performanceFeeTypeCore = performanceFeeTypeGroup && performanceFeeTypeGroup.cores[0];

        setPerformanceMethodology(
          performanceMethodologyCore && (performanceMethodologyCore.val || performanceMethodologyCore.defaultValue)
        );
        setPerformanceFeeType(
          performanceFeeTypeCore && (performanceFeeTypeCore.val || performanceFeeTypeCore.defaultValue)
        );
        return {
          performanceMethodology: performanceMethodologyCore
            && (performanceMethodologyCore.val || performanceMethodologyCore.defaultValue),
          performanceFeeType: performanceFeeTypeCore && (performanceFeeTypeCore.val || performanceFeeTypeCore.defaultValue),
        };
      })
      .then(initParams)
      .then(checkCanShare)
      .then(initTimePeriods)
      .then((_maxDate) => setBillingInvoice(_maxDate))
      .then(() => setLoading(false))
      .catch(err => {
        handleError(parseErrorMessage(err));
      });
  }, [props.periods]);

  useEffect(() => {
    setCanGenerate(canGenerate());
  }, [params]);

  // ETODO loading as prop?
  if (loading) {
    return <LoadingSpinner/>;
  }

  return <form id={'modularReportParameters'} onSubmit={generateReport}>
    <Button importance={'secondary'}
            size={'md'}
            label={'Save as Default Settings'}
            disabled={false}
            action={saveSettings}/>
    <SectionTitle title={'General'}/>
    {showStartAndPeriod() && <RadioSet dataPoint={{name: 'timeType', value: timeType}}
                                       onChange={(name, value) => {
                                         updateParameterValue('startDate', timePeriod.start);
                                         setTimeType(value);
                                       }}
                                       options={[
                                         {label: 'Fixed Time Periods', value: 'FIXED'},
                                         {label: 'Custom Time Periods', value: 'CUSTOM'},
                                       ]}
                                       orientation={'row'}/>}
    {timeType === 'CUSTOM' && <Row>
      <DatePicker title={'Start Date'}
                  width={'100px'}
                  layout={{grow: 1}}
                  dataPoint={{name: 'startDate', value: params.startDate.value}}
                  onChange={updateParameterValue}
                  minDate={minDate}
                  maxDate={yesterday}/>

      <DatePicker title={'End Date'}
                  width={'100px'}
                  layout={{grow: 1}}
                  dataPoint={{name: 'asOfDate', value: params.asOfDate.value}}
                  onChange={(name, value) => {
                    updateParameterValue(name, value);
                    setUnrealizedGainsDisabled(value);
                    setBillingInvoice(value);
                  }}
                  minDate={minDate}
                  maxDate={yesterday}/>
    </Row>}
    {timeType === 'FIXED' && <Row justifySubRow={'left'}>
      <DatePicker title={'As of Date'}
                  width={'100px'}
                  layout={{grow: 1}}
                  dataPoint={{name: 'asOfDate', value: params.asOfDate.value}}
                  onChange={(name, value) => {
                    updateParameterValue(name, value);
                    updateParameterValue('startDate', calculateFixedStartDate(minDate, value, timePeriod));
                    setUnrealizedGainsDisabled(value);
                    setBillingInvoice(value);
                  }}
                  minDate={minDate}
                  maxDate={yesterday}/>
      {showStartAndPeriod()
      && <Dropdown onChange={changeTimePeriod}
                   title={'Time Period'}
                   options={periods.map(period => ({label: period.name, value: period}))}
                   width={'100px'}
                   layout={{grow: 1}}
                   isSearchable={true}
                   dataPoint={{
                     name: 'timePeriod',
                     value: {
                       label: timePeriod.name,
                       value: timePeriod,
                     },
                   }}
                   placeholder={'Select a Period'}/>}
    </Row>}
    {renderCheckbox('overviewPage')}
    {renderCheckbox('activitySummaryPage')}

    {renderCheckbox('multiPeriodPerformanceTablePage', perfReportEntitlement ? undefined : {type: 'tooltip', description: 'If selected, this will replace the Activity Summary.'})}
    {renderCheckbox('multiPeriodPerformanceGraphPage')}

    {detailedContext !== 'ACCOUNT' && renderCheckbox('accountsPage')}
    {detailedContext !== 'ACCOUNT' && isEntitled([{group: 'reports', feature: 'netWorth', access: 'r'}]) && renderCheckbox('netWorthPage')}
    {!oldPerf && perfReport && <div>
      {renderCheckbox('activityGraphPage')}
      <SectionTitle title={'Activity Inclusions'}/>
      {renderCheckbox('netInflows')}
      {renderCheckbox('netOutflows')}
      {renderCheckbox('netReturn')}
      {renderCheckbox('interestPaid')}
      {renderCheckbox('dividendsPaid')}
      {renderCheckbox('advisoryFees')}
    </div>}
    {perfReport && <>
      {(isEntitled([
        {
          group: 'performance',
          feature: 'dwrTwrSelect',
          access: 'r',
        },
        {
          group: 'performance',
          feature: 'netOfFees',
          access: 'r',
        },
      ]) || entitledBenchmarks) && <>
        <SectionTitle title={'Performance Methodology'}/>
        {isEntitled([
          {group: 'performance', feature: 'dwrTwrSelect', access: 'r'},
          {group: 'performance', feature: 'netOfFees', access: 'r'},
        ], true) && <DescriptiveText>Select one choice in each column</DescriptiveText>}
        <Row wrap={false}>
          {isEntitled([
            {
              group: 'performance',
              feature: 'dwrTwrSelect',
              access: 'r',
            },
          ]) && <RadioSet dataPoint={{name: 'performanceMethodology', value: performanceMethodology}}
                          orientation={'column'}
                          options={[
                            {label: params.dollarWeighted.displayName, value: 'dwr'},
                            {label: params.timeWeighted.displayName, value: 'twr'},
                          ]} onChange={updatePerfMethodology}/>}
          {isEntitled([
            {
              group: 'performance',
              feature: 'netOfFees',
              access: 'r',
            },
          ]) && <RadioSet dataPoint={{name: 'performanceFeeType', value: performanceFeeType}}
                          orientation={'column'}
                          options={[
                            {label: params.grossFees.displayName, value: 'gross'},
                            {label: params.netFees.displayName, value: 'net'},
                          ]} onChange={updatePerfFeeType}/>}
        </Row>
        {entitledBenchmarks && <Row><BenchmarkSelector width={'fill'} showDisclosure={false} onChange={updateBenchmark}/></Row>}
      </>}
      {isEntitled([
        {
          group: 'risk',
          access: 'r',
        },
      ]) && <>
        <SectionTitle title={'Risk'}/>
        {renderCheckbox('portfolioRisk')}
      </>}
      <SectionTitle title={'Performance Breakdown'}/>
      <Row wrap={false}>
        {renderCheckbox('performersPage')}
        {renderCheckbox('contributorsPage')}
      </Row>
      {isEntitled([
        {
          group: 'performance',
          feature: 'assetClass',
          access: 'r',
        },
      ]) && <>
        <Row wrap={false}>
          {renderCheckbox('broadAssetClassPerformance')}
          {renderCheckbox('broadAssetClassContribution')}
        </Row>
        {isEntitled([
          {
            group: 'exposure',
            feature: 'broad',
            access: 'r',
          },
        ]) && <Row wrap={false}>
          {renderCheckbox('detailedAssetClassPerformance')}
          {renderCheckbox('detailedAssetClassContribution')}
        </Row>}
      </>}
    </>
    }
    <SectionTitle title={'Exposure'}/>
    {renderCheckbox('assetClassesPage')}
    {isEntitled([{group: 'exposure', feature: 'broad', access: 'r'}]) && renderCheckbox('assetClassDetailedPage')}
    {renderCheckbox('stockSectorPage')}
    {renderCheckbox('bondSectorPage')}
    {renderCheckbox('geographicPage')}
    {isEntitled([
      {group: 'reports', feature: 'targetExposure', access: 'r'},
      {group: 'exposure', feature: 'broad', access: 'r'},
    ], true) && renderCheckbox('targetExposurePage')}
    {!!params.targetExposurePage.value && <div>
      {renderTargetInput('stocksTarget')}
      {renderTargetInput('bondsTarget')}
      {renderTargetInput('cashTarget')}
      {renderTargetInput('alternativesTarget')}
    </div>}
    <SectionTitle title={'Holdings'}/>
    <Row wrap={false}>
      {renderCheckbox('holdingsByAccountPage')}
      {params.positionPerformance
        && ((params.holdingsByAccountPage && params.holdingsByAccountPage.value)
        || (params.holdingsByAssetClassPage && params.holdingsByAssetClassPage.value)) && renderCheckbox('positionPerformance')}
    </Row>
    {renderCheckbox('holdingsByAssetClassPage')}
    {isEntitled([
      {
        group: 'reports',
        feature: 'holdingsAnalysis',
        access: 'r',
      },
    ])
    && renderCheckbox('holdingsAnalysisPage')}
    {params.holdingsAnalysisPage.value && <>
      <SectionTitle title={'Included Data'}/>
      {renderCheckbox('quantity')}
      {renderCheckbox('price')}
      {renderCheckbox('marketValue')}
      {renderCheckbox('unrealizedGainLossValue')}
      {renderCheckbox('unrealizedGainLossPercentage')}
      {renderCheckbox('estimatedAnnualIncome')}
      {renderCheckbox('yield')}
    </>}
    {isEntitled([
      {
        group: 'reports',
        feature: 'income',
        access: 'r',
      },
    ])
    && <>
      <SectionTitle title={'Income'}/>
      {renderCheckbox('incomePage')}
      {params.incomePage && params.incomePage.value && <>
        <DescriptiveText alignment={'left'} margin={'10px 5px 10px 5px'}>Activity Inclusions</DescriptiveText>
        <Row wrap={false}>
          {renderCheckbox('incomeAdvisoryFee')}
          {renderCheckbox('incomeDividend')}
        </Row>
        <Row wrap={false}>
          {renderCheckbox('incomeFee')}
          {renderCheckbox('incomeInterest')}
        </Row>
      </>}
    </>
    }
    {isBillingInvoice && <>
      <SectionTitle title={'Billing'}/>
      {renderCheckbox('billingInvoice', {type: 'tooltip', description: 'The most recent BillFin invoice for each Household will be included in the report.'})}
    </>
    }
    {canShare && <>
      <SectionTitle title={'Sharing'}/>
      <Checkbox title={params.share.displayName}
                disabled={params.share.disabled}
                dataPoint={{name: params.share.name, value: !params.share.value || params.share.value !== 'NONE'}}
                onChange={(name, value) => {
                  updateParameterValue(name, value ? 'USER' : 'NONE');
                }}/>
    </>}
    {params.share.value && params.share.value !== 'NONE'
    && <RadioSet options={[{label: 'Share on CircleBlack', value: 'USER'}, {label: 'Share via Email', value: 'EMAIL'}]}
                 dataPoint={{name: 'share', value: params.share.value}}
                 onChange={updateParameterValue}/>}
  </form>;
};

const mapStateToProps = state => ({
  session: state.session,
  periods: state.periods,
  unreadReportsTimeoutID: state.badges.unreadReportsTimeoutID,
  unreadReports: state.badges.unreadReports,
  reportPageVisitTimestamp: state.badges.reportPageVisitTimestamp,
});

const mapDispatchToProps = (dispatch) => ({
  getPeriods: () => dispatch(_getPeriods()),
  openModal: modalToOpen => dispatch(_openModal(modalToOpen)),
  getUnreadReports: (timeoutID, unread, timestamp, interval) => dispatch(_getUnreadReports(timeoutID, unread, timestamp, interval)),
});

ModularReportParameters.propTypes = {
  report: PropTypes.object.isRequired,
  session: PropTypes.shape({
    profile: PropTypes.shape({
      id: PropTypes.number.isRequired,
      firm: PropTypes.shape({id: PropTypes.number.isRequired}),
    }).isRequired,
  }).isRequired,
  detailedContext: PropTypes.oneOf(['HOUSEHOLD', 'ACCOUNT', 'ADVISOR_GROUP']).isRequired,
  detailed: PropTypes.oneOfType([
    PropTypes.shape({
      containerID: PropTypes.number.isRequired,
      shareDisabled: PropTypes.bool,
    }),
    PropTypes.oneOf(['ALL']),
  ]).isRequired,
  initialContainerID: PropTypes.number.isRequired,
  periods: PropTypes.array.isRequired,
  getPeriods: PropTypes.func.isRequired,
  setCanGenerate: PropTypes.func.isRequired,
  setErrorMessage: PropTypes.func.isRequired,
  setSuccessMessage: PropTypes.func.isRequired,
  setStep: PropTypes.func.isRequired,
  handleError: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  setProcessing: PropTypes.func.isRequired,
  /**
   * Are from badges reducer
   */
  getUnreadReports: PropTypes.func,
  unreadReportsTimeoutID: PropTypes.object,
  unreadReports: PropTypes.object,
  reportPageVisitTimestamp: PropTypes.object,
  entity: PropTypes.object,
};

export default connect(mapStateToProps, mapDispatchToProps)(ModularReportParameters);
